import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  GripVertical,
  Crown,
  Wallet,
  PiggyBank,
  FileText,
  Heart,
  Calendar,
  BookOpen,
  type LucideIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

interface Guide {
  id: string;
  slug: string;
  title: string;
  short_description: string | null;
  icon: string;
  order_index: number;
  is_premium: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const iconOptions: { value: string; label: string; icon: LucideIcon }[] = [
  { value: 'Wallet', label: 'Wallet (Elterngeld)', icon: Wallet },
  { value: 'PiggyBank', label: 'PiggyBank (Kindergeld)', icon: PiggyBank },
  { value: 'FileText', label: 'FileText (Dokument)', icon: FileText },
  { value: 'Heart', label: 'Heart (Mutterschaft)', icon: Heart },
  { value: 'Calendar', label: 'Calendar (Elternzeit)', icon: Calendar },
  { value: 'BookOpen', label: 'BookOpen (Allgemein)', icon: BookOpen },
];

const getIcon = (iconName: string): LucideIcon => {
  const found = iconOptions.find((o) => o.value === iconName);
  return found?.icon || FileText;
};

export function GuidesPage() {
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();
  const [isCreating, setIsCreating] = useState(false);
  const [editingGuide, setEditingGuide] = useState<Guide | null>(null);

  // Fetch guides with stats
  const { data: guides, isLoading } = useQuery({
    queryKey: ['admin-guides-full', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guides')
        .select('*')
        .eq('locale', contentLocale)
        .order('order_index');
      if (error) throw error;
      return data as Guide[];
    },
  });

  // Create guide
  const createGuide = useMutation({
    mutationFn: async (data: Partial<Guide>) => {
      const { error } = await (supabase.from('guides') as any).insert({
        title: data.title,
        slug: data.slug,
        short_description: data.short_description,
        icon: data.icon,
        order_index: (guides?.length || 0) + 1,
        is_premium: data.is_premium || false,
        is_active: true,
        locale: contentLocale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guides-full'] });
      setIsCreating(false);
      toast.success('Guide erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Update guide
  const updateGuide = useMutation({
    mutationFn: async (data: Guide) => {
      const { error } = await (supabase.from('guides') as any)
        .update({
          title: data.title,
          slug: data.slug,
          short_description: data.short_description,
          icon: data.icon,
          is_premium: data.is_premium,
          is_active: data.is_active,
          locale: contentLocale,
        })
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guides-full'] });
      setEditingGuide(null);
      toast.success('Guide aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Delete guide
  const deleteGuide = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('guides').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guides-full'] });
      toast.success('Guide gelöscht');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Toggle active
  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await (supabase.from('guides') as any)
        .update({ is_active })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guides-full'] });
    },
  });

  // Toggle premium
  const togglePremium = useMutation({
    mutationFn: async ({ id, is_premium }: { id: string; is_premium: boolean }) => {
      const { error } = await (supabase.from('guides') as any)
        .update({ is_premium })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guides-full'] });
      toast.success('Premium-Status geändert');
    },
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Behördenguides</h1>
            <p className="text-slate-500 mt-1">
              Verwalte die Haupt-Guides (Elterngeld, Kindergeld, etc.)
            </p>
          </div>
          <LocaleSelector />
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg
                       hover:bg-teal-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Neuer Guide
          </button>
        </div>
      </div>

      {/* Create Form */}
      {isCreating && (
        <GuideForm
          onSubmit={(data) => createGuide.mutate(data)}
          onCancel={() => setIsCreating(false)}
          isLoading={createGuide.isPending}
        />
      )}

      {/* Guides List */}
      <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-100">
        {guides?.map((guide) => {
          const Icon = getIcon(guide.icon);

          if (editingGuide?.id === guide.id) {
            return (
              <GuideForm
                key={guide.id}
                guide={editingGuide}
                onSubmit={(data) => updateGuide.mutate({ ...editingGuide, ...data })}
                onCancel={() => setEditingGuide(null)}
                isLoading={updateGuide.isPending}
                isEditing
              />
            );
          }

          return (
            <div key={guide.id} className="flex items-center gap-4 p-4 hover:bg-slate-50">
              <button className="text-slate-400 cursor-grab">
                <GripVertical className="w-5 h-5" />
              </button>

              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  guide.is_active ? 'bg-teal-100' : 'bg-slate-100'
                }`}
              >
                <Icon
                  className={`w-6 h-6 ${guide.is_active ? 'text-teal-600' : 'text-slate-400'}`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${!guide.is_active && 'text-slate-400'}`}>
                    {guide.title}
                  </span>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                    /{guide.slug}
                  </span>
                  {guide.is_premium && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                      <Crown className="w-3 h-3" />
                      Premium
                    </span>
                  )}
                  {!guide.is_active && (
                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                      Inaktiv
                    </span>
                  )}
                </div>
                {guide.short_description && (
                  <p className="text-sm text-slate-500 truncate">{guide.short_description}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    togglePremium.mutate({
                      id: guide.id,
                      is_premium: !guide.is_premium,
                    })
                  }
                  className={`p-2 rounded-lg transition-colors ${
                    guide.is_premium
                      ? 'text-amber-600 hover:bg-amber-50'
                      : 'text-slate-400 hover:bg-slate-100'
                  }`}
                  title={guide.is_premium ? 'Premium entfernen' : 'Als Premium markieren'}
                >
                  <Crown className="w-4 h-4" />
                </button>
                <button
                  onClick={() =>
                    toggleActive.mutate({
                      id: guide.id,
                      is_active: !guide.is_active,
                    })
                  }
                  className={`p-2 rounded-lg transition-colors ${
                    guide.is_active
                      ? 'text-green-600 hover:bg-green-50'
                      : 'text-slate-400 hover:bg-slate-100'
                  }`}
                  title={guide.is_active ? 'Deaktivieren' : 'Aktivieren'}
                >
                  {guide.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setEditingGuide(guide)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (
                      confirm(
                        `Guide "${guide.title}" und alle zugehörigen Kategorien, Begriffe und FAQs löschen?`
                      )
                    ) {
                      deleteGuide.mutate(guide.id);
                    }
                  }}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}

        {guides?.length === 0 && (
          <div className="p-8 text-center text-slate-500">Noch keine Guides vorhanden</div>
        )}
      </div>
    </div>
  );
}

// Guide Form Component
interface GuideFormProps {
  guide?: Guide;
  onSubmit: (data: Partial<Guide>) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing?: boolean;
}

function GuideForm({ guide, onSubmit, onCancel, isLoading, isEditing }: GuideFormProps) {
  const [title, setTitle] = useState(guide?.title || '');
  const [slug, setSlug] = useState(guide?.slug || '');
  const [shortDescription, setShortDescription] = useState(guide?.short_description || '');
  const [icon, setIcon] = useState(guide?.icon || 'FileText');
  const [isPremium, setIsPremium] = useState(guide?.is_premium || false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ title, slug, short_description: shortDescription, icon, is_premium: isPremium });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <form onSubmit={handleSubmit} className="bg-teal-50 p-4 rounded-lg border border-teal-100">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Titel</label>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (!isEditing) setSlug(generateSlug(e.target.value));
            }}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="z.B. Elterngeld"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Slug</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="z.B. elterngeld"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Icon</label>
          <select
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          >
            {iconOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Beschreibung</label>
          <input
            type="text"
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="Kurze Beschreibung..."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Premium</label>
          <label className="flex items-center gap-2 h-[38px]">
            <input
              type="checkbox"
              checked={isPremium}
              onChange={(e) => setIsPremium(e.target.checked)}
              className="w-4 h-4 text-teal-500 border-slate-300 rounded
                         focus:ring-teal-500"
            />
            <span className="text-sm text-slate-600">Nur für Premium-Nutzer</span>
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-500 text-white rounded-lg
                     hover:bg-teal-600 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {isEditing ? 'Speichern' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}
